import { useTranslation } from 'react-i18next'

import { QA_NAME_CONFIRMATION } from '../../../../../constants/qa'
import {
  useDateLocale,
  useMassActions,
  useNotification,
  useTimeZone,
} from '../../../../../hooks'
import { ConfirmationModal } from '../../../../Confirmation'
import { performMockViewingsMassAction } from '../../../../RentalPortalViewing'
import { VIEWING_ACTION_ARCHIVE_MASS } from '../../RentalPortalViewings.constants'
import { getViewingsMassActions } from '../../RentalPortalViewings.utils'

import {
  getNotificationTitle,
  getViewingsMassActionDescription,
  getViewingsMassActionEligibleItems,
  getViewingsMassActionFailures,
  getViewingsMassActionFailureText,
  getViewingsMassActionItems,
  getViewingsMassActionRequest,
} from './useRentalPortalViewingsMassActions.utils'

import ArchiveIcon from '../../../../../icons/delete.svg'

export function useRentalPortalViewingsMassActions({
  allItems,
  excludeIds,
  filterValues,
  selectAllPages,
  selected,
  setModal,
  onCleanSelection,
  onClose,
  onReload,
  onUndo,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const { handleAddErrorNotification, handleAddWarningNotification } =
    useNotification()

  const { handleAddFinishMassActionNotification } = useMassActions({
    getNotificationTitle,
  })

  const massActionItems = getViewingsMassActionItems({
    allItems,
    excludeIds,
    selectAllPages,
    selected,
  })

  const eligibleItems = getViewingsMassActionEligibleItems(massActionItems)

  const massActions = getViewingsMassActions({
    eligibleCount: eligibleItems.length,
    selectedSize: massActionItems.length,
    t,
  })

  function addResultNotification({ action, resultItems, successIds, onRetry }) {
    const failures = getViewingsMassActionFailures({
      items: allItems,
      locale,
      resultItems,
      t,
      timezone,
    })

    if (failures.count === 0) {
      if (successIds.length === 1) {
        onUndo(successIds[0])

        return
      }

      handleAddFinishMassActionNotification(
        { items: resultItems, success_ids: successIds },
        { action, totalCount: resultItems.length }
      )

      return
    }

    const notification = {
      text: getViewingsMassActionFailureText({ failures, t }),
      title:
        successIds.length > 0
          ? getNotificationTitle({
              action,
              count: successIds.length,
              t,
              totalCount: resultItems.length,
            })
          : t('RentalPortal.ListingMassActionErrorKey'),
    }

    if (successIds.length > 0) {
      handleAddWarningNotification(notification)

      return
    }

    handleAddErrorNotification({
      ...notification,
      textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
      onClickTextButton: onRetry,
    })
  }

  function massAction({ action, ids, onRetry }) {
    return performMockViewingsMassAction(
      getViewingsMassActionRequest({
        action,
        eligibleIds: eligibleItems.map(viewing => viewing.id),
        excludeIds,
        filterValues,
        ids,
        selectAllPages,
      })
    )
      .then(({ data }) => {
        onClose()

        addResultNotification({
          action,
          resultItems: data.items,
          successIds: data.success_ids,
          onRetry,
        })

        if (data.success_ids.length === 0) {
          return null
        }

        onCleanSelection()

        return onReload()
      })
      .catch(() => {
        onClose()

        handleAddErrorNotification({
          textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
          title: t('RentalPortal.ListingMassActionErrorKey'),
          onClickTextButton: onRetry,
        })
      })
  }

  function showMassConfirmModal({ action, submitIcon, submitText }) {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitProps: { beforeIconProps: { Icon: submitIcon } },
          submitText,
        }}
        description={getViewingsMassActionDescription({
          eligibleItems,
          locale,
          selectedCount: massActionItems.length,
          t,
          timezone,
        })}
        qaName={QA_NAME_CONFIRMATION}
        onCancel={onClose}
        onSubmit={() =>
          massAction({ action, onRetry: () => setMassAction(action) })
        }
      />
    )
  }

  function setMassAction(action) {
    if (eligibleItems.length === 0) {
      return null
    }

    switch (action) {
      case VIEWING_ACTION_ARCHIVE_MASS:
        showMassConfirmModal({
          action,
          submitIcon: ArchiveIcon,
          submitText: t('RentalPortal.ViewingArchiveActionOptionKey'),
        })
        break
      default:
        return null
    }
  }

  return { massAction, massActions, setMassAction }
}
